// client/src/components/history/EventHistoryArticle.jsx
//
// Tenant editor for one event's public "article":
//   * a public/hidden toggle + editable intro paragraph (seeded from the
//     event's own data — description + spelled-out date)
//   * each media file with its own public/hidden toggle and an editable
//     caption (seeded from the social-post caption it was posted with), shown
//     in chronological order of being made public.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaHome, FaSave, FaEye, FaEyeSlash, FaExternalLinkAlt, FaTh, FaList, FaUndo } from "react-icons/fa";
import AppFooter from "../AppFooter";
import MediaViewer from "../media/MediaViewer";
import {
  getEventArticle, saveEventArticle, saveFileCaption, toggleFilePublicVisibility,
} from "../../services/historyApi";
import { ARTICLE_TEMPLATES, DEFAULT_ARTICLE_TEMPLATE } from "../../constants/articleTemplates";

const isVideoMime = (m) => /^video\//i.test(m || "");

export default function EventHistoryArticle() {
  const { tenant, eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [event, setEvent] = useState(null);
  const [generatedIntro, setGeneratedIntro] = useState("");

  const [introText, setIntroText] = useState("");
  const [introPublic, setIntroPublic] = useState(false);
  const [introDirty, setIntroDirty] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_ARTICLE_TEMPLATE);
  const [templateSaving, setTemplateSaving] = useState(false);

  // media: [{ fileId, url, mimetype, kind, itemName, createdOn, modifiedOn,
  //           showInPublicHistory, caption, defaultCaption }]
  const [media, setMedia] = useState([]);
  const [captions, setCaptions] = useState({});        // fileId -> current text
  const [savedCaptions, setSavedCaptions] = useState({}); // fileId -> persisted text
  const [togglingId, setTogglingId] = useState(null);

  const [mediaViewer, setMediaViewer] = useState(null); // { url, isVideo }
  const [mediaViewMode, setMediaViewMode] = useState(() => {
    try { return localStorage.getItem("os_historyarticle_media_view") || "single"; } catch { return "single"; }
  });
  const setMediaViewModeAndPersist = (mode) => {
    setMediaViewMode(mode);
    try { localStorage.setItem("os_historyarticle_media_view", mode); } catch {}
  };

  const [status, setStatus] = useState(null); // { type: 'success'|'error', text }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.classList.add("eventlist-fullbleed");
    return () => document.body.classList.remove("eventlist-fullbleed");
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await getEventArticle(tenant, eventId);
      setEvent(data.event);
      setGeneratedIntro(data.generatedIntro || "");
      const serverIntro = data.event?.intro == null ? "" : data.event.intro;
      setIntroText(serverIntro || data.generatedIntro || "");
      setIntroPublic(!!data.event?.introPublic);
      setIntroDirty(false);
      setTemplate(data.event?.template || DEFAULT_ARTICLE_TEMPLATE);

      const list = Array.isArray(data.media) ? data.media : [];
      setMedia(list);
      const cap = {};
      const savedCap = {};
      list.forEach((m) => {
        // Show the saved caption, or the default as a starting point. Seed the
        // "saved" baseline to the same text so a blur without an edit is a
        // no-op — we only POST once the tenant actually changes something.
        cap[m.fileId] = m.caption || m.defaultCaption || "";
        savedCap[m.fileId] = cap[m.fileId];
      });
      setCaptions(cap);
      setSavedCaptions(savedCap);
    } catch (e) {
      setErr(e?.message || "Failed to load event article");
    } finally {
      setLoading(false);
    }
  }, [tenant, eventId]);

  useEffect(() => { load(); }, [load]);

  // Public media in chronological order of being made public (modifiedOn asc),
  // then the not-yet-public media by upload order.
  const ordered = useMemo(() => {
    const ts = (v) => new Date(v || 0).getTime() || 0;
    const pub = media.filter((m) => m.showInPublicHistory).sort((a, b) => ts(a.modifiedOn) - ts(b.modifiedOn));
    const hid = media.filter((m) => !m.showInPublicHistory).sort((a, b) => ts(a.createdOn) - ts(b.createdOn));
    return { pub, hid };
  }, [media]);

  const handleToggleIntroPublic = () => { setIntroPublic((v) => !v); setIntroDirty(true); };

  const handleIntroChange = (v) => { setIntroText(v); setIntroDirty(true); };

  const resetIntroToDefault = () => { setIntroText(generatedIntro); setIntroDirty(true); };

  const persistIntro = async () => {
    // Save null when the text is empty or still exactly the generated default,
    // so the article keeps tracking the event's data until the tenant customises.
    const trimmed = (introText || "").trim();
    const intro = (!trimmed || trimmed === (generatedIntro || "").trim()) ? null : introText;
    const res = await saveEventArticle(tenant, eventId, { intro, introPublic, template });
    setIntroPublic(!!res.introPublic);
    setIntroDirty(false);
  };

  const handleSelectTemplate = async (id) => {
    if (id === template || templateSaving) return;
    const previous = template;
    setTemplate(id);
    setTemplateSaving(true);
    try {
      const trimmed = (introText || "").trim();
      const intro = (!trimmed || trimmed === (generatedIntro || "").trim()) ? null : introText;
      await saveEventArticle(tenant, eventId, { intro, introPublic, template: id });
    } catch (e) {
      setTemplate(previous);
      setStatus({ type: "error", text: `Could not change the template: ${e?.message || "please try again."}` });
    } finally {
      setTemplateSaving(false);
    }
  };

  const persistCaption = async (fileId) => {
    const text = captions[fileId] || "";
    if (text === (savedCaptions[fileId] || "")) return;
    await saveFileCaption(tenant, fileId, text);
    setSavedCaptions((prev) => ({ ...prev, [fileId]: text }));
    setMedia((prev) => prev.map((m) => (m.fileId === fileId ? { ...m, caption: text } : m)));
  };

  const handleToggleMedia = async (m) => {
    const next = !m.showInPublicHistory;
    setTogglingId(m.fileId);
    try {
      await toggleFilePublicVisibility(tenant, m.fileId, next);
      // Going public stamps modifiedon server-side; mirror that locally so the
      // card re-sorts to the end of the chronological list.
      setMedia((prev) => prev.map((x) => (
        x.fileId === m.fileId
          ? { ...x, showInPublicHistory: next, modifiedOn: new Date().toISOString() }
          : x
      )));
    } catch (e) {
      setStatus({ type: "error", text: `Could not update visibility: ${e?.message || "please try again."}` });
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setStatus(null);
    try {
      // template changes save immediately on click (handleSelectTemplate), but
      // Save also covers it in case that request is still in flight / failed.
      if (introDirty) {
        await persistIntro();
      } else {
        await saveEventArticle(tenant, eventId, { intro: event?.intro ?? null, introPublic, template });
      }
      const dirtyFiles = Object.keys(captions).filter(
        (id) => (captions[id] || "") !== (savedCaptions[id] || "")
      );
      for (const id of dirtyFiles) {
        // eslint-disable-next-line no-await-in-loop
        await saveFileCaption(tenant, Number(id), captions[id] || "");
      }
      setSavedCaptions((prev) => {
        const next = { ...prev };
        dirtyFiles.forEach((id) => { next[id] = captions[id] || ""; });
        return next;
      });
      setStatus({ type: "success", text: "Saved." });
    } catch (e) {
      setStatus({ type: "error", text: e?.message || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (m) => (
    <article
      key={`media-${m.fileId}`}
      className="feed-card"
      style={{ padding: 0, border: "1px solid rgba(0,0,0,0.15)", borderRadius: 12, overflow: "hidden" }}
    >
      <div
        style={{ cursor: "pointer" }}
        role="button"
        tabIndex={0}
        onClick={() => setMediaViewer({ url: m.url, isVideo: isVideoMime(m.mimetype) })}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setMediaViewer({ url: m.url, isVideo: isVideoMime(m.mimetype) })}
      >
        {isVideoMime(m.mimetype) ? (
          <video className="feed-media" src={m.url} preload="metadata" playsInline muted />
        ) : (
          <img className="feed-media" src={m.url} alt={m.itemName || "event media"} loading="lazy" />
        )}
      </div>

      <div className="history-media-card__controls">
        <span className="history-media-card__kind">
          {m.kind === "item" ? (m.itemName || "Event item") : "Event"}
        </span>
        <button
          type="button"
          className={`visibility-toggle visibility-toggle--${m.showInPublicHistory ? "public" : "hidden"}`}
          onClick={() => handleToggleMedia(m)}
          disabled={togglingId === m.fileId}
          aria-label={`${m.showInPublicHistory ? "Hide" : "Show"} media ${m.fileId}`}
          title={m.showInPublicHistory ? "Visible in public article" : "Hidden from public article"}
        >
          {m.showInPublicHistory ? <FaEye /> : <FaEyeSlash />}
          {m.showInPublicHistory ? "Public" : "Hidden"}
        </button>
      </div>
      <div className="history-media-card__caption">
        <textarea
          className="form-control"
          rows={2}
          value={captions[m.fileId] ?? ""}
          placeholder={m.defaultCaption || "Add a caption…"}
          onChange={(e) => setCaptions((prev) => ({ ...prev, [m.fileId]: e.target.value }))}
          onBlur={() => persistCaption(m.fileId).catch((e) => setStatus({ type: "error", text: `Caption save failed: ${e?.message || "please try again."}` }))}
          style={{ whiteSpace: "pre-wrap" }}
        />
      </div>
    </article>
  );

  if (loading) {
    return <div className="container"><p style={{ padding: 16, color: "#a8a8a8" }}>Loading…</p></div>;
  }
  if (err) {
    return (
      <div className="container">
        <div className="alert alert-danger" style={{ margin: 16 }}>{err}</div>
        <AppFooter buttons={[{ icon: <FaArrowLeft />, label: "Back", onClick: () => navigate(-1) }]} />
      </div>
    );
  }

  return (
    <div className="container">
      <h2 className="hdr hdr--primary hdr--lg">{event?.name || "Event"}</h2>

      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(`/${tenant}/eventHistory`)}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/dashboard`)}>
          <FaHome /> Home
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/${eventId}/publicEventArticle`)}>
          <FaExternalLinkAlt /> View public article
        </button>
        <button type="button" className="btn-primary-action" onClick={handleSaveAll} disabled={saving}>
          <FaSave /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {status && (
        <div className={`history-status-banner history-status-banner--${status.type}`}>
          {status.text}
        </div>
      )}

      <div className="history-panel">
        {/* Intro */}
        <div className="history-panel__header">
          <label className="history-panel__title" htmlFor="article-intro">Intro text</label>
          <button
            type="button"
            className={`visibility-toggle visibility-toggle--${introPublic ? "public" : "hidden"}`}
            onClick={handleToggleIntroPublic}
            aria-label="Toggle intro visibility"
            title={introPublic ? "Shown on the public article" : "Hidden from the public article"}
          >
            {introPublic ? <FaEye /> : <FaEyeSlash />}
            {introPublic ? "Public" : "Hidden"}
          </button>
        </div>
        <textarea
          id="article-intro"
          className="form-control"
          rows={5}
          value={introText}
          placeholder={generatedIntro}
          onChange={(e) => handleIntroChange(e.target.value)}
          onBlur={() => introDirty && persistIntro().catch((e) => setStatus({ type: "error", text: `Intro save failed: ${e?.message || "please try again."}` }))}
          style={{ whiteSpace: "pre-wrap" }}
        />
        <div className="history-panel__footer">
          <button type="button" className="ghost-btn" onClick={resetIntroToDefault}>
            <FaUndo /> Reset to default
          </button>
        </div>

        <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.08)", margin: "18px 0" }} />

        {/* Template */}
        <div className="history-panel__header">
          <label className="history-panel__title" htmlFor="article-template">Article template</label>
          {templateSaving && <span style={{ color: "#a8a8a8", fontSize: 12.5 }}>Saving…</span>}
        </div>
        <div className="history-template-row">
          {/* Not disabled while saving — a disabled <select> loses focus, which
              is what made an earlier button-grid picker look like it was
              "jumping" back to the first option mid-click. */}
          <select
            id="article-template"
            className="form-control"
            value={template}
            onChange={(e) => handleSelectTemplate(e.target.value)}
          >
            {ARTICLE_TEMPLATES.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.label}</option>
            ))}
          </select>
          <div className="history-template-swatch-wrap">
            <div className={`public-article-swatch public-article--${template}`}>Aa</div>
          </div>
        </div>
        <p className="history-panel__hint">
          {ARTICLE_TEMPLATES.find((t) => t.id === template)?.description}
        </p>
      </div>

      {/* Media */}
      <div className="media-column-header">
        <h3 style={{ margin: 0 }}>Media</h3>
        <div className="media-view-toggle" role="group" aria-label="Media view mode">
          <button
            type="button"
            className={`view-toggle-btn ${mediaViewMode === "single" ? "active" : ""}`}
            onClick={() => setMediaViewModeAndPersist("single")}
            aria-label="Single column view"
          >
            <FaList />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${mediaViewMode === "grid" ? "active" : ""}`}
            onClick={() => setMediaViewModeAndPersist("grid")}
            aria-label="Grid view"
          >
            <FaTh />
          </button>
        </div>
      </div>

      {media.length === 0 && <p style={{ padding: "0 12px", color: "#a8a8a8" }}>No media on this event yet.</p>}

      {ordered.pub.length > 0 && (
        <div className={`item-media-cards item-media-cards--${mediaViewMode}`}>
          {ordered.pub.map(renderCard)}
        </div>
      )}

      {ordered.hid.length > 0 && (
        <>
          <div className="history-not-public-divider">
            <hr />
            <span>Not yet public</span>
            <hr />
          </div>
          <div className={`item-media-cards item-media-cards--${mediaViewMode}`}>
            {ordered.hid.map(renderCard)}
          </div>
        </>
      )}

      {mediaViewer && (
        <MediaViewer
          src={mediaViewer.url}
          isVideo={mediaViewer.isVideo}
          onClose={() => setMediaViewer(null)}
        />
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: "Back", onClick: () => navigate(`/${tenant}/eventHistory`) },
          { icon: <FaHome />, label: "Home", onClick: () => navigate(`/${tenant}/dashboard`) },
          { icon: <FaSave />, label: saving ? "Saving…" : "Save", onClick: handleSaveAll, disabled: saving },
        ]}
      />
    </div>
  );
}
